import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  DollarSign, 
  Users, 
  ShoppingBag, 
  TrendingUp, 
  Activity,
  Database,
  Server,
  Globe,
  Shield,
  Zap,
  Crown,
  Settings,
  BarChart3,
  PieChart,
  LineChart,
  Monitor,
  Cpu,
  HardDrive,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  TrendingDown,
  Eye,
  RefreshCw,
  Download,
  Filter,
  Search,
  Bell,
  Lock,
  Unlock,
  UserCheck,
  FileText,
  Code,
  Terminal,
  Layers,
  MapPin,
  Sparkles
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Dashboard data interfaces
interface DashboardMetric {
  name: string;
  value: string | number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  status: 'good' | 'warning' | 'error';
  icon: React.ComponentType<any>;
}

interface SystemAlert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  resolved: boolean;
}

interface UserActivity {
  id: string;
  user: string;
  action: string;
  timestamp: Date;
  details: any;
}

export function AdminDashboard() {
  const { user, userData, updateUserData } = useAuth();
  const { toast } = useToast();
  const [isInitializing, setIsInitializing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetric[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);
  const [userActivities, setUserActivities] = useState<UserActivity[]>([]);
  const [realTimeMode, setRealTimeMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const isSuperAdmin = userData?.role === 'superadmin' && userData?.email === 'milan.krnjajic@gmail.com';
  const isAdmin = userData?.isAdmin || isSuperAdmin;

  useEffect(() => {
    if (isAdmin) {
      initializeDashboardData();
      if (realTimeMode) {
        const interval = setInterval(updateRealTimeData, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [isAdmin, realTimeMode, refreshTick]);

  const initializeDashboardData = async () => {
    setLoading(true);
    
    try {
      let liveUserCount = 1;
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        if (usersSnapshot && !usersSnapshot.empty) {
          liveUserCount = usersSnapshot.size;
        }
      } catch (err) {
        console.warn('Could not query users collection:', err);
      }

      // Generate dashboard metrics
      const metrics: DashboardMetric[] = [
        {
          name: 'Total Users',
          value: liveUserCount,
          change: 0,
          trend: 'stable',
          status: 'good',
          icon: Users
        },
        {
          name: 'Active Sessions',
          value: Math.max(1, Math.min(liveUserCount, 2)),
          change: 0,
          trend: 'stable',
          status: 'good',
          icon: Activity
        },
        {
          name: 'Revenue',
          value: '$2,847',
          change: 18.3,
          trend: 'up',
          status: 'good',
          icon: DollarSign
        },
        {
          name: 'System Load',
          value: '23%',
          change: 2.1,
          trend: 'up',
          status: 'good',
          icon: Cpu
        },
        {
          name: 'Storage Used',
          value: '67.2%',
          change: 1.8,
          trend: 'up',
          status: 'warning',
          icon: HardDrive
        },
        {
          name: 'API Calls',
          value: '1.2K',
          change: 25.4,
          trend: 'up',
          status: 'good',
          icon: Globe
        },
        {
          name: 'Error Rate',
          value: '0.02%',
          change: -15.6,
          trend: 'down',
          status: 'good',
          icon: AlertTriangle
        },
        {
          name: 'Uptime',
          value: '99.97%',
          change: 0.03,
          trend: 'stable',
          status: 'good',
          icon: CheckCircle
        }
      ];
      setDashboardMetrics(metrics);

      // Generate system alerts
      const alerts: SystemAlert[] = [
        {
          id: '1',
          type: 'warning',
          title: 'High Memory Usage',
          message: 'Memory usage is at 78%. Consider optimizing or scaling.',
          timestamp: new Date(Date.now() - 1800000),
          resolved: false
        },
        {
          id: '2',
          type: 'success',
          title: 'Backup Completed',
          message: 'Daily backup completed successfully at 3:00 AM.',
          timestamp: new Date(Date.now() - 3600000),
          resolved: true
        },
        {
          id: '3',
          type: 'info',
          title: 'New User Registration',
          message: '2 new users registered in the last hour.',
          timestamp: new Date(Date.now() - 900000),
          resolved: true
        }
      ];
      setSystemAlerts(alerts);

      // Generate user activities
      const activities: UserActivity[] = [
        {
          id: '1',
          user: 'milan.krnjajic@gmail.com',
          action: 'Logged in as Super Admin',
          timestamp: new Date(),
          details: { ip: '192.168.1.1', browser: 'Chrome' }
        },
        {
          id: '2',
          user: 'system',
          action: 'Database backup completed',
          timestamp: new Date(Date.now() - 1800000),
          details: { size: '45.2 MB', duration: '2.3s' }
        },
        {
          id: '3',
          user: 'user@example.com',
          action: 'Created new post',
          timestamp: new Date(Date.now() - 3600000),
          details: { postId: 'post_123', category: 'general' }
        }
      ];
      setUserActivities(activities);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRealTimeData = () => {
    setDashboardMetrics(prev => prev.map(metric => ({
      ...metric,
      value: typeof metric.value === 'number' 
        ? Math.max(0, metric.value + (Math.random() - 0.5) * 2)
        : metric.value,
      change: metric.change + (Math.random() - 0.5) * 2,
      trend: Math.random() > 0.6 ? 'up' : Math.random() > 0.3 ? 'down' : 'stable'
    })));
  };

  const initializeAdminAccess = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be logged in.',
        variant: 'destructive',
      });
      return;
    }

    setIsInitializing(true);
    
    try {
      const adminData = {
        role: user.email === 'milan.krnjajic@gmail.com' ? 'superadmin' as const : 'admin' as const,
        isAdmin: true,
        isSeller: true,
        permissions: user.email === 'milan.krnjajic@gmail.com' ? [
          'super_admin',
          'admin_dashboard',
          'user_management',
          'content_moderation',
          'system_settings',
          'financial_oversight',
          'analytics_access',
          'security_management',
          'platform_configuration',
          'emergency_controls',
          'audit_logs',
          'developer_tools'
        ] : [
          'admin_dashboard',
          'user_management',
          'content_moderation',
          'analytics_access'
        ],
        adminInitialized: true,
        adminInitializedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, adminData, { merge: true });
      await updateUserData(adminData);

      toast({
        title: 'Admin Initialized',
        description: `Welcome! You now have ${user.email === 'milan.krnjajic@gmail.com' ? 'Super Admin' : 'Admin'} access.`,
      });

      setRefreshTick((prev) => prev + 1);

    } catch (error) {
      console.error('Error initializing admin:', error);
      toast({
        title: 'Initialization Failed',
        description: 'Failed to initialize admin access.',
        variant: 'destructive',
      });
    } finally {
      setIsInitializing(false);
    }
  };

  if (loading && isAdmin) {
    return (
      <div className="dashboard-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Dashboard</h2>
          <p className="text-gray-600">Initializing admin interface...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="dashboard-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-600 rounded-3xl mb-8 motion-enter reveal-1">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute inset-0">
              <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-10 right-10 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-2xl">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full mb-2 text-sm">
                    <Crown className="w-3 h-3" />
                    <span>Admin Dashboard</span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                    Initialize Admin Access
                  </h1>
                  <p className="text-lg text-white/90 leading-relaxed">Get admin privileges to access the dashboard</p>
                </div>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Admin Access Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                <h3 className="font-semibold mb-4">Current Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Email:</span>
                    <Badge variant="outline">{user?.email || 'Not logged in'}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Role:</span>
                    <Badge className="bg-gray-100 text-gray-700">{userData?.role || 'user'}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Admin Access:</span>
                    <Badge className="bg-red-100 text-red-700">Not Initialized</Badge>
                  </div>
                </div>
              </div>

              <Button
                onClick={initializeAdminAccess}
                disabled={!user || isInitializing}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              >
                {isInitializing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Initializing Admin Access...
                  </>
                ) : (
                  <>
                    <Unlock className="w-4 h-4 mr-2" />
                    Initialize Admin Access
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-slate-900 dark:text-slate-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-800 via-blue-700 to-cyan-700 rounded-3xl mb-8 motion-enter reveal-1">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-10 right-10 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-300/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl flex items-center justify-center shadow-2xl">
                  {isSuperAdmin ? <Crown className="w-10 h-10 text-white" /> : <Shield className="w-10 h-10 text-white" />}
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-3 text-sm">
                    <Sparkles className="w-4 h-4" />
                    <span>{isSuperAdmin ? 'Super Admin Dashboard' : 'Admin Dashboard'}</span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
                    Advanced Control Center
                  </h1>
                  <p className="text-base md:text-xl text-white/90 leading-relaxed">Real-time platform monitoring and management</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <div>
                      <div className="text-sm font-medium">{isSuperAdmin ? 'Super Admin' : 'Admin'} Active</div>
                      <div className="text-xs text-white/70">All systems operational</div>
                    </div>
                  </div>
                </div>
                {isSuperAdmin && (
                  <Button 
                    onClick={() => { window.location.href = '/admin/super-admin-dashboard'; }}
                    className="bg-amber-400 hover:bg-amber-500 text-slate-950 font-bold rounded-xl"
                  >
                    <Crown className="w-4 h-4 mr-1.5" />
                    Super Admin Hub
                  </Button>
                )}
                <Button 
                  onClick={() => setRealTimeMode(!realTimeMode)}
                  className={`${realTimeMode ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'} text-white font-semibold rounded-xl`}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  {realTimeMode ? 'Live' : 'Paused'}
                </Button>
                <Button 
                  onClick={() => setRefreshTick((prev) => prev + 1)}
                  className="bg-white/90 hover:bg-white text-gray-900 font-semibold rounded-xl"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardMetrics.slice(0, 4).map((metric, index) => (
            <Card key={metric.name} className="border-0 shadow-xl bg-white/90 backdrop-blur-sm overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    metric.status === 'good' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                    metric.status === 'warning' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                    'bg-gradient-to-r from-red-500 to-orange-500'
                  }`}>
                    <metric.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center gap-1">
                    {metric.trend === 'up' && <TrendingUp className="w-4 h-4 text-green-500" />}
                    {metric.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
                    {metric.trend === 'stable' && <Activity className="w-4 h-4 text-blue-500" />}
                    <span className={`text-sm font-medium ${
                      metric.trend === 'up' ? 'text-green-600' :
                      metric.trend === 'down' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {metric.change > 0 ? '+' : ''}{metric.change.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                  <div className="text-sm text-gray-600">{metric.name}</div>
                  <Progress 
                    value={typeof metric.value === 'string' && metric.value.includes('%') 
                      ? parseFloat(metric.value) 
                      : Math.min(typeof metric.value === 'number' ? metric.value * 10 : 50, 100)} 
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Advanced Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-white/80 backdrop-blur-sm h-12">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              System
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* System Alerts */}
              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    System Alerts ({systemAlerts.filter(a => !a.resolved).length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {systemAlerts.slice(0, 5).map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border-l-4 ${
                          alert.type === 'error' ? 'bg-red-50 border-red-500' :
                          alert.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                          alert.type === 'success' ? 'bg-green-50 border-green-500' :
                          'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                className={
                                  alert.type === 'error' ? 'bg-red-100 text-red-700' :
                                  alert.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                  alert.type === 'success' ? 'bg-green-100 text-green-700' :
                                  'bg-blue-100 text-blue-700'
                                }
                              >
                                {alert.type.toUpperCase()}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {alert.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                            <div className="font-medium text-sm">{alert.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{alert.message}</div>
                          </div>
                          {!alert.resolved && (
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {userActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{activity.action}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {activity.user} • {activity.timestamp.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dashboardMetrics.slice(4).map((metric) => (
                <Card key={metric.name} className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        metric.status === 'good' ? 'bg-green-100 text-green-600' :
                        metric.status === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-red-100 text-red-600'
                      }`}>
                        <metric.icon className="w-5 h-5" />
                      </div>
                      <Badge className={
                        metric.status === 'good' ? 'bg-green-100 text-green-700' :
                        metric.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {metric.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xl font-bold text-gray-900">{metric.value}</div>
                      <div className="text-sm text-gray-600">{metric.name}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        {metric.trend === 'up' && <><TrendingUp className="w-3 h-3 text-green-500" /> +{metric.change.toFixed(1)}%</>}
                        {metric.trend === 'down' && <><TrendingDown className="w-3 h-3 text-red-500" /> {metric.change.toFixed(1)}%</>}
                        {metric.trend === 'stable' && <><Activity className="w-3 h-3 text-blue-500" /> Stable</>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Traffic Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Analytics data visualization</p>
                    <p className="text-sm text-gray-400">Real-time traffic monitoring</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    User Demographics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <PieChart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">User distribution analysis</p>
                    <p className="text-sm text-gray-400">Geographic and demographic data</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="w-5 h-5" />
                    Performance Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <LineChart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Performance metrics over time</p>
                    <p className="text-sm text-gray-400">Historical trend analysis</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Users className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">User Management Panel</h3>
                  <p className="text-gray-500 mb-6">Comprehensive user administration and analytics</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-md mx-auto">
                    <Button variant="outline" className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      View Users
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Permissions
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="system" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    System Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium">Database</span>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Healthy</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium">API Services</span>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        <span className="font-medium">Memory Usage</span>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700">High</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Database Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Total Documents:</span>
                      <span className="font-medium">86</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Storage Used:</span>
                      <span className="font-medium">13.4 MB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Read Operations:</span>
                      <span className="font-medium">1,247</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Write Operations:</span>
                      <span className="font-medium">89</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Shield className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Security Management</h3>
                  <p className="text-gray-500 mb-6">Monitor and manage platform security</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                    <Button variant="outline" className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Access Logs
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Threat Monitor
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Reports & Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <FileText className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Generate Reports</h3>
                  <p className="text-gray-500 mb-6">Create detailed platform reports and analytics</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto">
                    <Button variant="outline" className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      User Report
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Performance
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>{isSuperAdmin ? 'Super Admin' : 'Admin'} Session Active</span>
            </div>
            <div>Last Updated: {new Date().toLocaleString()}</div>
          </div>
          <p>InTouch Advanced Admin Dashboard • {user?.email} • {isSuperAdmin ? 'Super Admin' : 'Admin'} Access</p>
        </div>
      </div>
    </div>
  );
}